<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'

// Optional third action: keep the progress and leave. Passing `saveLabel` turns
// the prompt into "save / discard / stay" — saving becomes the primary action and
// leaving without it drops to a secondary one, since losing work should never be
// the most prominent choice when keeping it is on offer.
const props = defineProps<{
  visible: boolean
  title?: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  saveLabel?: string
  /** The save is in flight — every action waits for it. */
  saving?: boolean
}>()

const emit = defineEmits<{
  (e: 'confirm' | 'cancel' | 'save'): void
}>()

function cancel() {
  if (!props.saving) emit('cancel')
}
</script>

<template>
  <Box
    v-if="visible"
    flex
    align="center"
    justify="center"
    class="confirm-leave-overlay"
    @click.self="cancel"
  >
    <Box direction="col" gap="3" class="confirm-leave-modal">
      <Text type="heading" is="h3">{{ title ?? 'Leave?' }}</Text>
      <Text type="body" color="muted">
        {{ message ?? 'Are you sure you want to leave?' }}
      </Text>
      <Button
        v-if="saveLabel"
        :label="saveLabel"
        :loading="saving"
        block
        class="confirm-leave-modal__actions"
        @click="emit('save')"
      />
      <Box flex gap="2" :class="{ 'confirm-leave-modal__actions': !saveLabel }">
        <Button
          :label="cancelLabel ?? 'Stay Here'"
          variant="outline"
          :disabled="saving"
          class="confirm-leave-modal__btn"
          @click="cancel"
        />
        <Button
          :label="confirmLabel ?? 'Leave'"
          :variant="saveLabel ? 'outline' : 'solid'"
          :disabled="saving"
          class="confirm-leave-modal__btn"
          @click="emit('confirm')"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.confirm-leave-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  padding: 1.25rem;
  background: rgba(17, 24, 39, 0.45);
  backdrop-filter: blur(4px);
}

.confirm-leave-modal {
  width: 100%;
  max-width: 26rem;
  padding: 1.5rem;
  border-radius: 20px;
  background: var(--p-surface-0);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
}

.confirm-leave-modal__actions {
  margin-top: 0.25rem;
}

.confirm-leave-modal__btn {
  flex: 1;
  justify-content: center !important;
}
</style>
